/* ─────────────────────────────────────────────────────────────
   lala / cart-drawer.js
   Slide-in cart drawer with async add/update/remove,
   A4→A3 upsell prompt, shipping progress bar.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  var LA = window.lala = window.lala || {};

  var drawer, overlay, body, subtotalEls, countEl, shipTxtEl, shipFillEl, upsellHost, emptyHost;
  var state = { open: false, cart: null, threshold: 0, currency: 'EUR' };
  var lastRemoved = null; // for undo

  function init() {
    drawer = document.getElementById('la-cart-drawer');
    overlay = document.getElementById('la-cart-overlay');
    if (!drawer) return;
    body = drawer.querySelector('[data-drawer-body]');
    subtotalEls = drawer.querySelectorAll('[data-drawer-subtotal]');
    countEl = drawer.querySelector('[data-drawer-count]');
    shipTxtEl = drawer.querySelector('[data-ship-txt]');
    shipFillEl = drawer.querySelector('[data-ship-fill]');
    upsellHost = drawer.querySelector('[data-drawer-upsell]');
    emptyHost = drawer.querySelector('[data-drawer-empty]');
    state.threshold = parseInt(drawer.dataset.freeShippingThreshold || '0', 10) * 100; // in cents

    // Openers
    document.querySelectorAll('[data-cart-open]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });
    // Closers
    drawer.querySelectorAll('[data-cart-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });
    overlay && overlay.addEventListener('click', close);
    LA.on(document, 'keydown', function (e) { if (e.key === 'Escape' && state.open) close(); });

    // Refresh from current Shopify cart on load
    fetchCart().then(render);

    // Subscribe to add events from product form
    LA.listen('cart:added', function (d) {
      if (d && d.cart) { state.cart = d.cart; render(); open(); }
      else fetchCart().then(function (c) { render(); open(); });
    });
  }

  // `cart-open` on <body> lets other fixed UI (e.g. the popup widget) get out
  // of the way while the drawer is open.
  function open() { if (!drawer) return; drawer.classList.add('is-open'); overlay && overlay.classList.add('is-open'); document.body.classList.add('cart-open'); document.body.style.overflow = 'hidden'; state.open = true; drawer.setAttribute('aria-hidden', 'false'); var close = drawer.querySelector('[data-cart-close]'); close && close.focus(); }
  function close() { if (!drawer) return; drawer.classList.remove('is-open'); overlay && overlay.classList.remove('is-open'); document.body.classList.remove('cart-open'); document.body.style.overflow = ''; state.open = false; drawer.setAttribute('aria-hidden', 'true'); }

  function fetchCart() {
    return LA.fetchJSON('/cart.js').then(function (c) { state.cart = c; return c; });
  }

  function render() {
    var cart = state.cart;
    if (!cart) return;
    // Count
    if (countEl) countEl.textContent = pluralize(cart.item_count, drawer.dataset.itemsOne, drawer.dataset.itemsFew, drawer.dataset.itemsMany);
    // Sync every cart-btn pip on the page
    document.querySelectorAll('.la-cart-btn__pip').forEach(function (pip) { pip.textContent = cart.item_count; pip.style.display = cart.item_count ? '' : 'none'; });

    // Empty state
    if (cart.item_count === 0) {
      body.innerHTML = '';
      upsellHost.innerHTML = '';
      emptyHost.hidden = false;
      setSubtotal(0);
      if (shipTxtEl) shipTxtEl.innerHTML = '';
      if (shipFillEl) shipFillEl.style.width = '0%';
      return;
    }
    emptyHost.hidden = true;

    // Lines
    body.innerHTML = cart.items.map(renderLine).join('');
    wireLineEvents();

    // Subtotal (renders in both the totals row and the checkout button)
    setSubtotal(cart.total_price);

    // Shipping bar
    if (state.threshold > 0 && shipTxtEl && shipFillEl) {
      var remaining = state.threshold - cart.total_price;
      var pct = Math.min(100, Math.round((cart.total_price / state.threshold) * 100));
      shipFillEl.style.width = pct + '%';
      if (remaining <= 0) {
        shipTxtEl.innerHTML = '<span>' + (drawer.dataset.shipMet || 'Free shipping unlocked') + '</span>';
      } else {
        var tmpl = drawer.dataset.shipProgress || '%amount% to free shipping';
        shipTxtEl.innerHTML = '<span>' + tmpl.replace('%amount%', '<b>' + LA.money(remaining) + '</b>') + '</span><span>' + LA.money(cart.total_price) + ' / ' + LA.money(state.threshold) + '</span>';
      }
    }

    // Upsell (A4 → A3)
    renderUpsell();
  }

  function renderLine(item) {
    var props = item.properties || {};
    var propRows = '';
    var photos = [];
    var photoLabel = '';
    Object.keys(props).forEach(function (k) {
      if (!props[k] || k.charAt(0) === '_') return;
      var v = props[k];
      // Collect file uploads so all thumbnails render together on ONE row,
      // instead of stacking one labelled row per photo.
      if (/\.(jpe?g|png|webp|gif|heic|heif)(\?|$)/i.test(v) || v.indexOf('cdn.shopify.com') !== -1 || /\/uploads\//i.test(v)) {
        photos.push(v);
        if (!photoLabel) photoLabel = k.replace(/\s*\d+\s*$/, ''); // "Photo 1" -> "Photo"
      } else {
        propRows += '<b>' + escapeHtml(k) + '</b><span class="la-line__quote">' + escapeHtml(v) + '</span>';
      }
    });
    if (photos.length) {
      // links to the full file; gradient box shows through if the image can't
      // display (e.g. HEIC) and onerror drops the broken img.
      var thumbs = photos.map(function (v) {
        return '<a class="la-line__pic" href="' + escapeHtml(v) + '" target="_blank" rel="noopener">' +
          '<img src="' + escapeHtml(v) + '" alt="" loading="lazy" onerror="this.remove()"></a>';
      }).join('');
      propRows += '<b>' + escapeHtml(photoLabel || 'Photos') + '</b><span class="la-line__pics">' + thumbs + '</span>';
    }
    var img = item.image ? '<img src="' + item.image.replace('.jpg', '_200x.jpg') + '" alt="" loading="lazy">' : '';
    return (
      '<div class="la-line" data-line-key="' + item.key + '">' +
        '<div class="la-line__img">' + img + '</div>' +
        '<div class="la-line__body">' +
          '<div class="la-line__name"><a href="' + item.url + '">' + escapeHtml(item.product_title) + (item.variant_title && item.variant_title !== 'Default Title' ? ' · ' + escapeHtml(item.variant_title) : '') + '</a></div>' +
          '<div class="la-line__meta">' + propRows + '</div>' +
          '<div class="la-line__qty" data-qty-ctrl>' +
            '<button type="button" data-qty-dec aria-label="−">−</button>' +
            '<span data-qty-val>' + item.quantity + '</span>' +
            '<button type="button" data-qty-inc aria-label="+">+</button>' +
          '</div>' +
        '</div>' +
        '<div class="la-line__right">' +
          '<div class="la-line__price">' + LA.money(item.final_line_price) + '</div>' +
          '<button type="button" class="la-line__remove" data-remove>' + (drawer.dataset.removeLabel || 'remove') + '</button>' +
        '</div>' +
      '</div>'
    );
  }

  function wireLineEvents() {
    body.querySelectorAll('.la-line').forEach(function (line) {
      var key = line.dataset.lineKey;
      var dec = line.querySelector('[data-qty-dec]');
      var inc = line.querySelector('[data-qty-inc]');
      var val = line.querySelector('[data-qty-val]');
      var rm = line.querySelector('[data-remove]');
      dec && dec.addEventListener('click', function () { updateItem(key, Math.max(0, parseInt(val.textContent, 10) - 1), line); });
      inc && inc.addEventListener('click', function () { updateItem(key, parseInt(val.textContent, 10) + 1, line); });
      rm && rm.addEventListener('click', function () { removeItem(key, line); });
    });
  }

  function updateItem(key, qty, lineEl) {
    lineEl && lineEl.style.setProperty('opacity', '.5');
    return LA.fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (cart) { state.cart = cart; render(); })
      .catch(function () { lineEl && lineEl.style.removeProperty('opacity'); });
  }

  function removeItem(key, lineEl) {
    // capture for undo
    var item = (state.cart.items || []).filter(function (x) { return x.key === key; })[0];
    if (item) lastRemoved = { id: item.product_id, variant: item.variant_id, quantity: item.quantity, properties: item.properties || {} };
    updateItem(key, 0, lineEl).then(function () {
      LA.flash(drawer.dataset.removedFlash || 'Removed', {
        undo: lastRemoved ? restoreLast : null,
        undoLabel: drawer.dataset.undoLabel || 'undo'
      });
    });
  }

  function restoreLast() {
    if (!lastRemoved) return;
    var data = { id: lastRemoved.variant, quantity: lastRemoved.quantity, properties: lastRemoved.properties };
    LA.fetchJSON('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) })
      .then(fetchCart).then(render);
    lastRemoved = null;
  }

  // ───── A4 → A3 upsell ─────
  function renderUpsell() {
    if (!upsellHost || drawer.dataset.enableUpsell !== 'true') return;
    var a4Item = null;
    (state.cart.items || []).forEach(function (it) {
      if (/A4/i.test(it.variant_title || '')) a4Item = it;
    });
    if (!a4Item) { upsellHost.innerHTML = ''; return; }
    // Look up the A3 variant via product json
    LA.fetchJSON(a4Item.url.split('?')[0] + '.js').then(function (product) {
      var a3 = product.variants.filter(function (v) { return /A3/i.test(v.title) && v.available; })[0];
      if (!a3) { upsellHost.innerHTML = ''; return; }
      // Price difference A4 → A3, in the cart's PRESENTMENT currency.
      // product.js prices are always in the shop's BASE currency, but the A4
      // line price (from cart.js) is already in the presentment currency. So
      // we derive the effective rate from the A4 line itself and apply it to
      // the base difference — correct in every Market currency, no hardcoding.
      var a4Variant = product.variants.filter(function (v) { return v.id === a4Item.variant_id; })[0];
      var baseA4 = a4Variant ? a4Variant.price : a4Item.price;            // base cents
      var presentA4 = (a4Item.original_price != null ? a4Item.original_price : a4Item.price); // presentment cents
      var baseDiff = a3.price - baseA4;
      var priceDiff = baseA4 > 0 ? Math.round(baseDiff * (presentA4 / baseA4)) : baseDiff;
      if (priceDiff < 0) priceDiff = 0;
      var upsellTitle = (drawer.dataset.upsellTitle || 'Go <em>A3</em>? +%diff%').replace('%diff%', LA.money(priceDiff));
      upsellHost.innerHTML =
        '<div class="la-drawer__upsell">' +
          '<div class="la-upsell__ic"></div>' +
          '<div class="la-upsell__body">' +
            '<div class="la-upsell__title">' + upsellTitle + '</div>' +
            '<div class="la-upsell__sub">' + (drawer.dataset.upsellSub || 'Bigger frame, more air around the work.') + '</div>' +
          '</div>' +
          '<button type="button" class="la-upsell__add" data-upsell-swap data-a4-key="' + a4Item.key + '" data-a3-id="' + a3.id + '">' + (drawer.dataset.upsellAdd || '+ Add') + '</button>' +
        '</div>';
      var btn = upsellHost.querySelector('[data-upsell-swap]');
      btn.addEventListener('click', function () {
        btn.disabled = true;
        var props = a4Item.properties || {};
        // Add A3 FIRST, then remove A4, and render only once at the end — so
        // the cart never momentarily hits the empty state when A4 was the only
        // line. We call the cart APIs directly (not updateItem) to avoid the
        // intermediate re-render that caused the empty-cart flash.
        LA.fetchJSON('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: a3.id, quantity: a4Item.quantity, properties: props })
        }).then(function () {
          return LA.fetchJSON('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: a4Item.key, quantity: 0 })
          });
        }).then(function (cart) {
          state.cart = cart;
          render();
        }).catch(function () {
          btn.disabled = false;
          fetchCart().then(render);
        });
      });
    }).catch(function () { upsellHost.innerHTML = ''; });
  }

  // ───── helpers ─────
  function setSubtotal(price) {
    if (!subtotalEls) return;
    var formatted = LA.money(price);
    subtotalEls.forEach(function (el) { el.textContent = formatted; });
  }
  function pluralize(n, one, few, many) {
    // LT-aware simple pluralization; falls back on English "one/other" if only two forms provided
    if (!one) return n;
    var abs = Math.abs(n) % 100, last = abs % 10;
    if (abs === 1) return (one || '').replace('%count%', n);
    if (last >= 2 && last <= 9 && !(abs >= 11 && abs <= 19)) return (few || one || '').replace('%count%', n);
    return (many || few || one || '').replace('%count%', n);
  }
  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]; });
  }

  // ───── boot ─────
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
